import React, { useEffect, useState } from 'react';
import {
  FaAd, FaFacebookSquare,
  FaClock, FaRegEye, FaArrowRight,
  FaHistory
} from 'react-icons/fa';
import {
  HiOutlineRefresh, HiOutlineCog
} from 'react-icons/hi';
import { toast } from 'react-toastify';
import Calling from '../../api/Calling';
import generateCaptcha from '../../components/captcha';
import Loader from '../../components/Loader';
import { SiGoogleads } from "react-icons/si";
import { IoTimerOutline } from 'react-icons/io5';
import { TbNetwork } from "react-icons/tb";
import { useNavigate } from 'react-router-dom';

const SettingCard = ({
  title,
  icon,
  iconBgColor,
  description,
  children
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 hover:shadow-md border border-gray-100">
      <div className="p-5 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2.5 ${iconBgColor} rounded-lg`}>
              {icon}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{title}</h2>
              {description && (
                <p className="text-xs text-gray-500 mt-0.5">{description}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-5 bg-gray-50/50">
        {children}
      </div>
    </div>
  );
};

const ToggleSetting = ({
  title,
  description,
  icon,
  iconColor = "text-blue-500",
  isEnabled,
  onToggle,
  isDisabled = false
}) => {
  return (
    <div className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white ${isDisabled ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          {icon && React.cloneElement(icon, { className: iconColor, size: 18 })}
        </div>
        <div>
          <h3 className="font-medium text-gray-800">{title}</h3>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <label className={`relative inline-flex items-center ${isDisabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
        <input
          type="checkbox"
          checked={isEnabled}
          onChange={onToggle}
          className="sr-only peer"
          disabled={isDisabled}
        />
        <div className={`relative w-11 h-6 bg-gray-200 rounded-full peer 
          peer-checked:after:translate-x-full peer-checked:after:border-white 
          after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
          after:bg-white after:border-gray-300 after:border after:rounded-full 
          after:h-5 after:w-5 after:transition-all dark:border-gray-600 
          peer-checked:bg-main-blue`}></div>
      </label>
    </div>
  );
};

const NumberSetting = ({
  title,
  description,
  icon,
  iconColor = "text-blue-500",
  value,
  onChange,
  onUpdate,
  min = 0,
  isDisabled = false
}) => {
  return (
    <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-gray-200 rounded-lg bg-white ${isDisabled ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <div className="pt-0.5">
          {icon && React.cloneElement(icon, { className: iconColor, size: 18 })}
        </div>
        <div>
          <h3 className="font-medium text-gray-800">{title}</h3>
          {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        <input
          type="number"
          value={value}
          min={min}
          onChange={(e) => onChange(Number(e.target.value))}
          className={`w-24 p-2 border border-gray-200 rounded-lg text-center focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-transparent ${isDisabled ? 'bg-gray-100' : 'bg-white'}`}
          disabled={isDisabled}
        />
        <button
          onClick={onUpdate}
          className={`px-4 py-2 bg-main-blue text-white rounded-lg transition-colors duration-200 hover:bg-main-blue/80 focus:outline-none focus:ring-2 focus:ring-offset-2 ${isDisabled ? 'cursor-not-allowed opacity-70 bg-gray-400 hover:bg-gray-400' : ''}`}
          disabled={isDisabled}
        >
          Update
        </button>
      </div>
    </div>
  );
};

const InputSetting = ({
  title,
  description,
  icon,
  iconColor = "text-main-blue",
  value,
  onChange,
  placeholder = "",
  isDisabled = false
}) => {
  return (
    <div className={`${isDisabled ? 'opacity-60' : ''}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon && React.cloneElement(icon, { className: iconColor, size: 16 })}
        <label className="block text-sm font-medium text-gray-700">
          {title}
        </label>
      </div>
      {description && <p className="text-xs text-gray-500 mb-2">{description}</p>}
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-main-blue focus:border-transparent ${isDisabled ? 'bg-gray-100' : 'bg-white'}`}
        placeholder={placeholder}
        disabled={isDisabled}
      />
    </div>
  );
};


const Controller = () => {
  const [loader, setLoader] = useState(false);

  // Facebook integration
  const [facebook_app_id, setFacebook_app_id] = useState('');
  const [facebook_client_token, setFacebook_client_token] = useState('');

  // Ad integration
  const [ad_application_id, setAd_application_id] = useState('');
  const [inHouseAds_status, setInHouseAds_status] = useState(false);

  const [guide_video, setGuide_video] = useState(false);

  // AI Model settings
  const [aiModel_status, setAiModel_status] = useState(true);
  const [proMod_ad_count, setProMod_ad_count] = useState(0);


  // Genrate and Pro
  const [genrate_unloack_rwd, setGenrate_unloack_rwd] = useState('RWD');
  const [pro_unloack_rwd, setPro_unloack_rwd] = useState('RWD');

  // Story ads settings
  const [story_ads_pos, setStory_ads_pos] = useState(2);
  const [story_ads_time, setStory_ads_time] = useState(2);
  const [story_ads_url, setStory_ads_url] = useState('');
  const [story_ads_status, setStory_ads_status] = useState(true);

  const [banner_status, setBanner_status] = useState(true);
  const [story_status, setStory_status] = useState(true);

  // Ad transition settings
  const [intro_screen_ad, setIntro_screen_ad] = useState('OFF');
  const [pp_to_wel_ad, setPp_to_wel_ad] = useState('OFF');
  const [wel_to_home_ad, setWel_to_home_ad] = useState('OFF');
  const [home_to_cat_ad, setHome_to_cat_ad] = useState('OFF');
  const [cat_to_list_ad, setCat_to_list_ad] = useState('OFF');
  const [home_to_list_ad, setHome_to_list_ad] = useState('OFF');
  const [intent_controle, setIntent_controle] = useState('BOTH');

  const navigate = useNavigate()
  // Ad options
  const adOptions = [
    { value: 'OFF', label: 'OFF' },
    { value: 'FS', label: 'Full Screen' },
    { value: 'OPEN', label: 'Open' },
    { value: 'INHOUSE', label: 'In-House' },
    { value: 'FS_WEB', label: 'Fs-Web' },
  ];

  // Ad options
  const adOptions1 = [
    { value: 'RWD', label: 'Reward' },
    { value: 'RWD_WEB', label: 'Rwd-Web' }
  ];

  // Intent control options
  const intentOptions = [
    { value: 'BOTH', label: 'Both' },
    { value: 'FORWARD', label: 'Forward' },
    { value: 'BACKWARD', label: 'Backward' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getControlleData();
      const { status, message, data } = resp;
      if (status) {
        // Set all data with careful handling of undefined values
        setFacebook_app_id(data?.facebook_app_id || '');
        setFacebook_client_token(data?.facebook_client_token || '');
        setAd_application_id(data?.ad_application_id || '');
        setInHouseAds_status(data?.inHouseAds_status !== undefined ? data?.inHouseAds_status : false);
        setGuide_video(data?.guide_video !== undefined ? data?.guide_video : false);
        setAiModel_status(data?.aiModel_status !== undefined ? data?.aiModel_status : true);
        setProMod_ad_count(data?.proMod_ad_count !== undefined ? data?.proMod_ad_count : 0);
        setBanner_status(data?.banner_status !== undefined ? data?.banner_status : true);
        setStory_status(data?.story_status !== undefined ? data?.story_status : true);

        // Set ad transition settings
        setIntro_screen_ad(data?.intro_screen_ad || 'OFF');

        setPp_to_wel_ad(data?.pp_to_wel_ad || 'OFF');
        setWel_to_home_ad(data?.wel_to_home_ad || 'OFF');
        setHome_to_cat_ad(data?.home_to_cat_ad || 'OFF');
        setCat_to_list_ad(data?.cat_to_list_ad || 'OFF');
        setHome_to_list_ad(data?.home_to_list_ad || 'OFF')
        setIntent_controle(data?.intent_controle || 'BOTH');

        // Genrate and Pro
        setGenrate_unloack_rwd(data?.genrate_unloack_rwd || 'RWD');
        setPro_unloack_rwd(data?.pro_unloack_rwd || 'RWD');

        // Story ads settings
        setStory_ads_pos(data?.story_ads_pos !== undefined ? data?.story_ads_pos : 2);
        setStory_ads_time(data?.story_ads_time !== undefined ? data?.story_ads_time : 2);
        setStory_ads_url(data?.story_ads_url || '');

      } else {
        toast.error(message || "Failed to load settings");
      }
    } catch (error) {
      console.error("Error fetching controller data:", error);
      toast.error('An error occurred while fetching data');
    } finally {
      setLoader(false);
    }
  };

  const handleControllerPosition = async (name, value) => {

    const content = `You are about to update ${name}. To confirm, please solve this math problem`;
    const check = generateCaptcha(content);
    if (!check) return toast.error('Answer is not correct', { autoClose: 2000 });


    setLoader(true);
    try {
      const resp = await Calling.updateControllerPosition({ name, status: value });
      if (resp.status) toast.success(resp.message);
      else toast.error(resp.message);
    } catch {
      toast.error('Failed to update controller settings');
    } finally {
      fetchData();
      setLoader(false);
    }
  };

  const handleControllerStory = async (e) => {
    e.preventDefault();

    const content = `You are about to update story ads settings. To confirm, please solve this math problem`;
    const check = generateCaptcha(content);
    if (!check) return toast.error("Answer is not correct", { autoClose: 2000 });

    if (!story_ads_url.trim()) {
      return toast.error("Story Ads URL cannot be empty", { autoClose: 2000 });
    }

    if (story_ads_time < 5) {
      return toast.error("Story Ads Time must be at least 5", { autoClose: 2000 });
    }
    if (story_ads_pos < 0) {
      return toast.error("Story Ads Position must be at least 0", { autoClose: 2000 });
    }

    setLoader(true);
    try {
      const resp = await Calling.updateControllerStory({
        story_ads_url,
        story_ads_time,
        story_ads_pos,
        story_ads_status,
      });
      if (resp.status) toast.success(resp.message);
      else toast.error(resp.message);
    } catch {
      toast.error("Failed to update story ads settings");
    } finally {
      fetchData();
      setLoader(false);
    }
  };


  const handleFacebook = async (e) => {
    e.preventDefault();

    const content = `You are about to update Facebook settings. To confirm, please solve this math problem`;
    const check = generateCaptcha(content);
    if (!check) return toast.error('Answer is not correct', { autoClose: 2000 });

    setLoader(true);
    try {
      const resp = await Calling.setControleFacebook({ facebook_app_id, facebook_client_token });
      if (resp?.status) toast.success(resp?.message);
      else toast.error(resp?.message);
    } catch {
      toast.error('Failed to update Facebook settings');
    } finally {
      fetchData();
      setLoader(false);
    }
  }

  const handleAdApplicationId = async (e) => {
    e.preventDefault();

    // Validate the ad application ID isn't empty
    if (!ad_application_id.trim()) {
      return toast.error('Ad Application ID cannot be empty', { autoClose: 2000 });
    }

    const content = `You are about to update Ad Application ID. To confirm, please solve this math problem`;
    const check = generateCaptcha(content);
    if (!check) return toast.error('Answer is not correct', { autoClose: 2000 });

    setLoader(true);
    try {
      const resp = await Calling.updateControllerPosition({ name: 'ad_application_id', status: ad_application_id });
      if (resp?.status) toast.success(resp?.message);
      else toast.error(resp?.message);
    } catch {
      toast.error('Failed to update Ad Application ID');
    } finally {
      setLoader(false);
    }
  }

  if (loader) {
    return <Loader loader={loader} />;
  }

  const adTransitionItems = [
    {
      label: "Intro Screen Ad",
      icon: <FaArrowRight className="text-main-blue" />,
      value: intro_screen_ad,
      handler: (e) => setIntro_screen_ad(e.target.value),
      options: adOptions,
      saveHandler: () => handleControllerPosition('intro_screen_ad', intro_screen_ad),
    },
    {
      label: "Privacy Policy to Welcome",
      icon: <FaArrowRight className="text-main-blue" />,
      value: pp_to_wel_ad,
      handler: (e) => setPp_to_wel_ad(e.target.value),
      options: adOptions,
      saveHandler: () => handleControllerPosition('pp_to_wel_ad', pp_to_wel_ad),
    },
    {
      label: "Welcome to Home",
      icon: <FaArrowRight className="text-main-blue" />,
      value: wel_to_home_ad,
      handler: (e) => setWel_to_home_ad(e.target.value),
      options: adOptions,
      saveHandler: () => handleControllerPosition('wel_to_home_ad', wel_to_home_ad),
    },
    {
      label: "Home to Category",
      icon: <FaArrowRight className="text-main-blue" />,
      value: home_to_cat_ad,
      handler: (e) => setHome_to_cat_ad(e.target.value),
      options: adOptions,
      saveHandler: () => handleControllerPosition('home_to_cat_ad', home_to_cat_ad),
    },
    {
      label: "Category to Product",
      icon: <FaArrowRight className="text-main-blue" />,
      value: cat_to_list_ad,
      handler: (e) => setCat_to_list_ad(e.target.value),
      options: adOptions,
      saveHandler: () => handleControllerPosition('cat_to_list_ad', cat_to_list_ad),
    },
    {
      label: "Home to Product",
      icon: <FaArrowRight className="text-main-blue" />,
      value: home_to_list_ad,
      handler: (e) => setHome_to_list_ad(e.target.value),
      options: adOptions,
      saveHandler: () => handleControllerPosition('home_to_list_ad', home_to_list_ad),
    },
    {
      label: "Intent Control",
      icon: <FaArrowRight className="text-main-blue" />,
      value: intent_controle,
      handler: (e) => setIntent_controle(e.target.value),
      options: intentOptions,
      saveHandler: () => handleControllerPosition('intent_controle', intent_controle),
    },
  ];
  const adTransitionItems1 = [
    {
      label: "Pro Unloack",
      icon: <FaArrowRight className="text-main-blue" />,
      value: pro_unloack_rwd,
      handler: (e) => setPro_unloack_rwd(e.target.value),
      options: adOptions1,
      saveHandler: () => handleControllerPosition('pro_unloack_rwd', pro_unloack_rwd),
    },
    {
      label: "Genrate Prompt",
      icon: <FaArrowRight className="text-main-blue" />,
      value: genrate_unloack_rwd,
      handler: (e) => setGenrate_unloack_rwd(e.target.value),
      options: adOptions1,
      saveHandler: () => handleControllerPosition('genrate_unloack_rwd', genrate_unloack_rwd),
    },
  ];

  return (
    <div className="bg-gray-50 w-full min-h-screen px-4 py-6 mt-16 sm:px-6 lg:px-8 lg:ml-[4.4rem]">
      <div className="mx-auto">

        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Controller Settings</h1>
            <p className="mt-1 text-sm text-gray-500">Manage your application settings and features</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={fetchData}
              className="flex items-center gap-2 px-4 py-2 bg-main-blue hover:bg-main-blue/80 rounded-lg text-white transition-colors duration-200 shadow-sm"
            >
              <HiOutlineRefresh className="text-lg" />
              <span>Refresh</span>
            </button>
            <button
              onClick={() => navigate('/manage/ads/txt')}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
            >
              Manage Ads.txt
            </button>
          </div>
        </div>

        {/* Main content */}
        <div className="grid grid-cols-1 gap-4">
          {/* Ads Story Section */}
          <SettingCard
            title="Story Management"
            description="Configure all ad-related settings"
            icon={<FaHistory className="text-main-blue text-xl" />}
            iconBgColor="bg-main-blue/10"
          >
            <ToggleSetting
              title="Story Module"
              description="Enable or disable story module functionality"
              icon={<FaRegEye />}
              iconColor="text-main-blue"
              isEnabled={story_status}
              onToggle={() => handleControllerPosition('story_status', !story_status)}
            />
            {
              story_status &&
              <form onSubmit={handleControllerStory} className="space-y-4 mt-4">
                {/* ✅ Toggle Button */}
                <div
                  className={`flex items-center justify-between p-4 border border-gray-200 rounded-lg bg-white ${!story_ads_status ? "opacity-60" : ""
                    }`}
                >
                  <div className="flex items-start gap-3">
                    <div className="pt-0.5">
                      <SiGoogleads className="text-main-blue" size={18} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-800">Enable Story Ads</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Toggle story ads on or off
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={story_ads_status}
                      onChange={() => setStory_ads_status(!story_ads_status)}
                      className="sr-only peer"
                    />
                    <div
                      className="relative w-11 h-6 bg-gray-200 rounded-full peer 
                peer-checked:after:translate-x-full peer-checked:after:border-white 
                after:content-[''] after:absolute after:top-0.5 after:start-[2px] 
                after:bg-white after:border-gray-300 after:border after:rounded-full 
                after:h-5 after:w-5 after:transition-all 
                peer-checked:bg-main-blue"
                    ></div>
                  </label>
                </div>

                {/* ✅ Editable fields (only if enabled) */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Story Ads URL
                    </label>
                    <input
                      type="text"
                      className="w-full p-2 border rounded-md"
                      value={story_ads_url}
                      onChange={(e) => setStory_ads_url(e.target.value)}
                      disabled={!story_ads_status}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Story Ads Position
                    </label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-md"
                      value={story_ads_pos}
                      onChange={(e) => setStory_ads_pos(Number(e.target.value))}
                      disabled={!story_ads_status}
                      min={0}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Story Ads Time (seconds)
                    </label>
                    <input
                      type="number"
                      className="w-full p-2 border rounded-md"
                      value={story_ads_time}
                      onChange={(e) => setStory_ads_time(Number(e.target.value))}
                      disabled={!story_ads_status}
                      min={5}
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="px-4 py-2 bg-main-blue text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                >
                  {loader ? "Saving..." : "Save Settings"}
                </button>

              </form>
            }

          </SettingCard>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Ads Manager Section */}
            <SettingCard
              title="Ad Management"
              description="Configure all ad-related settings"
              icon={<FaAd className="text-main-blue text-xl" />}
              iconBgColor="bg-main-blue/10"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                  <ToggleSetting
                    title="In-House Ads"
                    description="Display in-app promotional content"
                    icon={<FaAd />}
                    iconColor="text-main-blue"
                    isEnabled={inHouseAds_status}
                    onToggle={() => handleControllerPosition('inHouseAds_status', !inHouseAds_status)}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <NumberSetting
                    title="Pro Model Ad Count"
                    description="Number of ads to display for pro model"
                    icon={<FaAd />}
                    iconColor="text-main-blue"
                    value={proMod_ad_count}
                    onChange={setProMod_ad_count}
                    onUpdate={() => handleControllerPosition('proMod_ad_count', proMod_ad_count)}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <ToggleSetting
                    title="Guide Video"
                    description="Guide video for the app"
                    icon={<FaAd />}
                    iconColor="text-main-blue"
                    isEnabled={guide_video}
                    onToggle={() => handleControllerPosition('guide_video', !guide_video)}
                  />
                </div>
              </div>
            </SettingCard>

            {/* AI Model Settings */}
            <SettingCard
              title="AI Model Settings"
              description="Configure AI model related settings"
              icon={<HiOutlineCog className="text-main-blue text-xl" />}
              iconBgColor="bg-main-blue/10"
            >
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4">
                  <ToggleSetting
                    title="AI Model Status"
                    description="Enable or disable AI model functionality"
                    icon={<FaRegEye />}
                    iconColor="text-main-blue"
                    isEnabled={aiModel_status}
                    onToggle={() => handleControllerPosition('aiModel_status', !aiModel_status)}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4">
                  <ToggleSetting
                    title="Banner Status"
                    description="Enable or disable banner functionality"
                    icon={<FaRegEye />}
                    iconColor="text-main-blue"
                    isEnabled={banner_status}
                    onToggle={() => handleControllerPosition('banner_status', !banner_status)}
                  />
                </div>
              </div>
            </SettingCard>


          </div>

          {/* Ad Transition Settings */}
          <SettingCard
            title="Ad Transition Settings"
            description="Configure ad transition settings"
            icon={<FaArrowRight className="text-main-blue" />}
            iconBgColor="bg-main-blue/10"
          >
            <div className="border-t pt-2 border-gray-200">
              <h3 className="text-md font-semibold text-gray-800 mb-2">Ad Transition Settings</h3>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {adTransitionItems.map((item, index) => (
                  <div key={index} className="flex flex-col justify-between p-3 sm:p-4 md:p-6 border border-main-border rounded-xl h-full bg-white">
                    <span className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base text-main-font mb-3 flex-shrink-0">
                      {item.icon}
                      <span className="truncate">{item.label}</span>
                    </span>
                    <div className="flex flex-col gap-3 mt-auto">
                      <select
                        className="border border-main-border rounded-lg p-2 bg-white text-main-font text-sm sm:text-base focus:ring-2 focus:ring-main-blue w-full"
                        value={item.value}
                        onChange={item.handler}
                      >
                        {item.options.map((option, idx) => (
                          <option key={idx} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className="px-3 sm:px-4 py-2 bg-main-blue text-main-white rounded hover:bg-main-blue/80 w-full text-sm sm:text-base"
                        onClick={item.saveHandler}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </SettingCard>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SettingCard
              title="Pro and Genrate Unloack RWD"
              description="Configure Pro image and Genrate prompt Unloack Reward ads"
              icon={<FaArrowRight className="text-main-blue" />}
              iconBgColor="bg-main-blue/10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {adTransitionItems1.map((item, index) => (
                  <div key={index} className="flex flex-col justify-between p-3 sm:p-4 md:p-6 border border-main-border rounded-xl h-full bg-white">
                    <span className="flex items-center gap-2 sm:gap-3 text-sm sm:text-base text-main-font mb-3 flex-shrink-0">
                      {item.icon}
                      <span className="truncate">{item.label}</span>
                    </span>
                    <div className="flex flex-col gap-3 mt-auto">
                      <select
                        className="border border-main-border rounded-lg p-2 bg-white text-main-font text-sm sm:text-base focus:ring-2 focus:ring-main-blue w-full"
                        value={item.value}
                        onChange={item.handler}
                      >
                        {item.options.map((option, idx) => (
                          <option key={idx} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <button
                        className="px-3 sm:px-4 py-2 bg-main-blue text-main-white rounded hover:bg-main-blue/80 w-full text-sm sm:text-base"
                        onClick={item.saveHandler}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </SettingCard>
          </div>


          {/* Integration Settings Section */}
          <SettingCard
            title="Integration Settings"
            description="Configure external services integration"
            icon={<HiOutlineCog className="text-main-blue text-xl" />}
            iconBgColor="bg-main-blue/10"
          >
            <div className="space-y-6">
              <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
                {/* Facebook Integration */}
                <div className="p-5 border border-gray-200 rounded-lg bg-white">
                  <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <FaFacebookSquare className="text-main-blue" />
                    Facebook Integration
                  </h3>

                  <div className="grid grid-cols-1 gap-4 mb-4">
                    <InputSetting
                      title="Facebook App ID"
                      description="Your Facebook application identifier"
                      icon={<FaFacebookSquare />}
                      iconColor="text-main-blue"
                      value={facebook_app_id}
                      onChange={setFacebook_app_id}
                      placeholder="e.g. 135885938132XXXX"
                    />

                    <InputSetting
                      title="Facebook Client Token"
                      description="Authentication token for Facebook API"
                      icon={<FaFacebookSquare />}
                      iconColor="text-main-blue"
                      value={facebook_client_token}
                      onChange={setFacebook_client_token}
                      placeholder="e.g. 964343XX-XXXX-XXXX-XXXX-XXXXXXXXXXXX"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleFacebook}
                      className="px-4 py-2 bg-main-blue text-white rounded-lg transition-colors duration-200 hover:bg-main-blue/80 focus:outline-none focus:ring-2 focus:ring-offset-2"
                    >
                      Save Facebook Settings
                    </button>
                  </div>
                </div>

                {/* Ad Integration */}
                <div className="p-5 border border-gray-200 rounded-lg bg-white">
                  <h3 className="text-lg font-medium text-gray-800 mb-4 flex items-center gap-2">
                    <FaAd className="text-main-blue" />
                    Ad Network Integration
                  </h3>

                  <div className="mb-4">
                    <InputSetting
                      title="Ad Application ID"
                      description="Identifier for your ad network integration"
                      icon={<FaAd />}
                      iconColor="text-main-blue"
                      value={ad_application_id}
                      onChange={setAd_application_id}
                      placeholder="e.g. ca-app-pub-xxxxxxxxxxxxxxxx"
                    />
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={handleAdApplicationId}
                      className="px-4 py-2 bg-main-blue text-white rounded-lg transition-colors duration-200 hover:bg-main-blue/80 focus:outline-none focus:ring-2 focus:ring-offset-2"
                    >
                      Save Ad Integration
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </SettingCard>

        </div>
      </div>
    </div>
  );
};

export default Controller;